import { Link } from 'react-router-dom'
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { blastCampaign, dryRunBlast, getBlastStatus, BlastCampaignResult, BlastAudienceMode as BlastAudience, EmailQueueRow } from '@/lib/workerApi'
import { logAdminAction } from '@/lib/audit-log'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, RefreshCw, Zap, RotateCcw, AlertTriangle, CheckCircle2, XCircle, Users, RefreshCwOff, ShieldAlert, ArrowRight, ArrowLeft, X, Radio, Mail, MailX, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Target, UserPlus, Repeat } from 'lucide-react';
import { DataPagination } from '@/components/DataPagination';
import { ViralRemakeBadge, isViralRemake } from '@/components/ViralRemakeBadge';
import { format, startOfDay, subDays, endOfDay } from 'date-fns';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CrCampaign {
  id: string
  brand_id: string
  name: string
  product_name: string
  status: string
  blast_status: string | null
  blast_recipient_count: number | null
  blast_sent_count: number | null
  blast_failed_count: number | null
  blast_queued_at: string | null
  blast_completed_at: string | null
  reblast_count: number | null
  created_at: string | null
  posted_at: string | null
  // Viral Video Remake job (read-only). Blast ops only needs to see which jobs
  // these are — the per-video remake instructions are brief review, handled on
  // the roster campaign pages. Older rows may not carry it: treat as false.
  is_viral_remake?: boolean | null
}

interface BrandConfig {
  id: string
  brand_name: string
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

const CAMPAIGN_STATUS_COLORS: Record<string, string> = {
  active:    'bg-green-100 text-green-800 border-green-200',
  paused:    'bg-yellow-100 text-yellow-800 border-yellow-200',
  completed: 'bg-gray-100 text-gray-700 border-gray-200',
}

const BLAST_STATUS_COLORS: Record<string, string> = {
  awaiting_blast: 'bg-slate-100 text-slate-700 border-slate-200',
  in_progress:    'bg-blue-100 text-blue-800 border-blue-200',
  queued:         'bg-indigo-100 text-indigo-800 border-indigo-200',
  blast_sent:     'bg-green-100 text-green-800 border-green-200',
  blast_failed:   'bg-red-100 text-red-800 border-red-200',
  blast_skipped:  'bg-orange-100 text-orange-800 border-orange-200',
}

// blast_status values that require force=true to re-run
const FORCE_REQUIRED_STATUSES = ['in_progress', 'queued', 'blast_sent']

// blast_status values that allow a normal (non-forced) blast
const BLASTABLE_STATUSES = ['awaiting_blast', 'blast_failed', 'blast_skipped', null]

function CampaignStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={CAMPAIGN_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'}>
      {status}
    </Badge>
  )
}

function BlastStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>
  return (
    <Badge variant="outline" className={BLAST_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'}>
      {status.replace(/_/g, ' ')}
    </Badge>
  )
}

// ── Blast dialog — two-step protected flow ───────────────────────────────────
//
// Step 1 (preview):  dialog opens → dry-run fires automatically → shows live
//                    recipient breakdown → "Proceed to confirm →" unlocks when
//                    eligible_recipients > 0.
//
// Step 2 (confirm):  danger-styled panel → admin must type the exact campaign
//                    name → "Fire blast" button enables only on exact match.
//                    If blast_status is in_progress/queued and force=false the
//                    fire button is blocked with an explanatory message.
//
// This means a real blast cannot be triggered with fewer than two intentional
// actions and a correctly typed name.

interface BlastDialogProps {
  campaign: CrCampaign
  brandName: string
  isForce: boolean
  onClose: () => void
  onBlasted: (campaignId: string, campaignName: string, recipientCount: number) => void
}

type DialogStep = 'preview' | 'confirm'

function PreviewRow({
  label,
  value,
  sub,
  highlight,
}: {
  label: string
  value: number
  sub?: string
  highlight?: boolean
}) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${highlight ? 'mt-1' : ''}`}>
      <span className={highlight ? 'text-sm font-semibold text-foreground' : 'text-sm text-muted-foreground'}>{label}</span>
      <div className="flex items-center gap-2">
        {sub && (
          <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">
            {sub}
          </span>
        )}
        <span className={`tabular-nums font-mono ${highlight ? (value === 0 ? 'text-muted-foreground text-base' : 'text-green-600 text-xl font-bold') : 'text-sm font-medium text-foreground'}`}>
          {value}
        </span>
      </div>
    </div>
  )
}

// ── Blast audience selector ───────────────────────────────────────────────────
//
// One control for the whole "who gets this blast" question — the three choices
// are mutually exclusive, so they live in a single radio group:
//
//   new_only  → matched audience, minus everyone already emailed or applied.
//   all       → matched audience, including prior recipients and prior
//               applicants (rejected ones included — they are otherwise locked
//               out of every future blast for this campaign).
//   manual    → exactly the hand-picked creators, category + gender targeting
//               skipped. Sent as audience:'all' + creator_ids, since picking a
//               creator by hand means emailing them regardless of history.

type AudienceChoice = BlastAudience | 'manual'

function AudienceSelect({
  value,
  onChange,
  excluded,
  selectedCount,
  disabled,
  children,
}: {
  value: AudienceChoice
  onChange: (v: AudienceChoice) => void
  /** Matched creators already emailed or already applied (union, from the worker). */
  excluded: number
  selectedCount: number
  disabled?: boolean
  /** Manual creator picker — revealed inline when 'manual' is chosen. */
  children?: React.ReactNode
}) {
  const options: { key: AudienceChoice; title: string; sub: string; Icon: typeof UserPlus; accent: 'primary' | 'orange' | 'amber' }[] = [
    {
      key: 'new_only',
      title: 'New creators only',
      sub: excluded > 0
        ? `Skips the ${excluded} already emailed or applied`
        : 'Skips anyone already emailed or applied',
      Icon: UserPlus,
      accent: 'primary',
    },
    {
      key: 'all',
      title: 'Everyone (new + previously contacted)',
      sub: excluded > 0
        ? `Adds back the ${excluded} previously emailed or applied`
        : 'Sends again to previous recipients and applicants',
      Icon: Repeat,
      accent: 'orange',
    },
    {
      key: 'manual',
      title: 'Specific creators',
      sub: selectedCount > 0
        ? `Emails exactly the ${selectedCount} creator${selectedCount !== 1 ? 's' : ''} you picked`
        : 'Hand-pick who gets the email, ignoring targeting',
      Icon: Target,
      accent: 'amber',
    },
  ]

  const ring = { primary: 'border-primary/40 bg-primary/5 ring-1 ring-primary/20', orange: 'border-orange-300 bg-orange-50 ring-1 ring-orange-200', amber: 'border-amber-300 bg-amber-50 ring-1 ring-amber-200' }
  const dot  = { primary: 'border-primary',    orange: 'border-orange-500', amber: 'border-amber-500' }
  const fill = { primary: 'bg-primary',        orange: 'bg-orange-500',     amber: 'bg-amber-500' }
  const text = { primary: 'text-primary',      orange: 'text-orange-600',   amber: 'text-amber-600' }

  return (
    <div className="rounded-xl border overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-b">
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Who gets this blast
        </span>
      </div>

      <div className="p-3 space-y-2">
        {options.map(({ key, title, sub, Icon, accent }) => {
          const active = value === key
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onChange(key)}
              className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed
                ${active ? ring[accent] : 'border-border hover:bg-muted/40'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`flex items-center justify-center h-4 w-4 rounded-full border shrink-0
                  ${active ? dot[accent] : 'border-muted-foreground/40'}`}>
                  {active && <span className={`h-2 w-2 rounded-full ${fill[accent]}`} />}
                </span>
                <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? text[accent] : 'text-muted-foreground'}`} />
                <span className="text-xs font-semibold leading-tight">{title}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug pl-6">{sub}</p>
            </button>
          )
        })}
      </div>

      {value === 'all' && (
        <div className="flex items-start gap-2 px-4 py-2.5 border-t bg-orange-50/60 text-xs text-orange-800">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-orange-500" />
          <span>
            Everyone the targeting matches gets this email, including creators who already received
            it and creators who already applied.
          </span>
        </div>
      )}

      {value === 'manual' && <div className="border-t">{children}</div>}
    </div>
  )
}

// ── Manual creator picker ─────────────────────────────────────────────────────
//
// Search roster_creators by name/email/@username and tick the ones to email.
// Selection is owned by BlastDialog so the dry-run preview + real blast can
// pass the chosen IDs. Reading roster_creators directly mirrors CreatorPayments.

interface RosterCreatorLite {
  id: string
  name: string | null
  email: string | null
  tiktok_username: string | null
}

function CreatorTestSelect({
  selected,
  onToggle,
  onClear,
}: {
  selected: Record<string, RosterCreatorLite>
  onToggle: (c: RosterCreatorLite) => void
  onClear: () => void
}) {
  const [input, setInput] = useState('')
  const [term, setTerm]   = useState('')

  // Debounce search input 300 ms
  useEffect(() => {
    const t = setTimeout(() => setTerm(input.trim()), 300)
    return () => clearTimeout(t)
  }, [input])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['blast-test-creator-search', term],
    enabled: term.length >= 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roster_creators')
        .select('id,name,email,tiktok_username')
        .not('email', 'is', null)
        .or(`name.ilike.%${term}%,email.ilike.%${term}%,tiktok_username.ilike.%${term}%`)
        .limit(25)
      if (error) throw error
      return (data ?? []) as RosterCreatorLite[]
    },
  })

  const selectedList = Object.values(selected)

  return (
    <div className="bg-amber-50/40 overflow-hidden">
      <div className="p-3 space-y-3">
        <p className="text-xs text-amber-700/80 leading-relaxed">
          These creators get the email and nobody else — category &amp; gender targeting are skipped,
          and they are sent to whether or not they were emailed or applied before.
        </p>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by name, email or @username…"
            value={input}
            onChange={e => setInput(e.target.value)}
            className="pl-8 h-9 bg-white"
          />
          {isFetching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Results */}
        {term.length >= 2 && (
          <ScrollArea className="h-40 rounded-md border bg-white">
            {results.length === 0 && !isFetching ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                No creators with an email matching “{term}”.
              </div>
            ) : (
              <div className="divide-y">
                {results.map(c => {
                  const checked = !!selected[c.id]
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                    >
                      <Checkbox checked={checked} onCheckedChange={() => onToggle(c)} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{c.name ?? c.tiktok_username ?? 'Unnamed'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.email}{c.tiktok_username ? ` · @${c.tiktok_username}` : ''}
                        </p>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </ScrollArea>
        )}
        {term.length > 0 && term.length < 2 && (
          <p className="text-xs text-muted-foreground">Type at least 2 characters to search.</p>
        )}

        {/* Selected chips */}
        {selectedList.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                {selectedList.length} selected
              </span>
              <button onClick={onClear} className="text-xs text-muted-foreground hover:text-foreground underline">
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedList.map(c => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-200 px-2 py-0.5 text-xs"
                >
                  <span className="truncate max-w-40">{c.name ?? c.email ?? c.tiktok_username}</span>
                  <button onClick={() => onToggle(c)} className="text-muted-foreground hover:text-foreground" aria-label="Remove">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function BlastDialog({ campaign, brandName, isForce, onClose, onBlasted }: BlastDialogProps) {
  const [step, setStep] = useState<DialogStep>('preview')
  const [preview, setPreview] = useState<BlastCampaignResult | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [nameInput, setNameInput] = useState('')

  // Blast audience — 'new_only' skips creators already emailed or already
  // applied (default); 'all' contacts them again alongside the new ones;
  // 'manual' targets exactly the hand-picked creators. 'manual' rides on the
  // 'all' wire value because hand-picking someone means emailing them
  // regardless of whether they were contacted before.
  const [choice, setChoice] = useState<AudienceChoice>('new_only')
  const isManual = choice === 'manual'
  const audience: BlastAudience = isManual ? 'all' : choice
  const includeAll = audience === 'all'

  // Manual creator selection. Keyed by id so toggling and de-duping is
  // trivial. selectedKey drives the preview effect's dependency.
  const [selected, setSelected] = useState<Record<string, RosterCreatorLite>>({})
  const selectedIds = Object.keys(selected)
  const selectedKey = selectedIds.join(',')
  // 'Specific creators' picked, but nothing ticked yet — no audience to preview.
  const manualEmpty = isManual && selectedIds.length === 0
  function toggleCreator(c: RosterCreatorLite) {
    setSelected(prev => {
      const next = { ...prev }
      if (next[c.id]) delete next[c.id]
      else next[c.id] = c
      return next
    })
  }

  // ── Step 1: fire dry-run automatically; re-run when the audience changes ───
  useEffect(() => {
    // 'Specific creators' with nothing ticked yet has no audience to preview —
    // and must NOT fall through to creator_ids:undefined, which the worker
    // would read as a full category blast.
    if (manualEmpty) {
      setPreview(null)
      setPreviewError(null)
      setPreviewing(false)
      return
    }
    let cancelled = false
    async function runPreview() {
      setPreviewing(true)
      setPreviewError(null)
      try {
        const ids = selectedKey ? selectedKey.split(',') : []
        const result = await dryRunBlast(campaign.id, {
          force: isForce,
          creator_ids: isManual && ids.length ? ids : undefined,
          audience,
        })
        if (!cancelled) setPreview(result)
      } catch (e: any) {
        if (!cancelled) setPreviewError(e.message ?? 'Preview failed')
      } finally {
        if (!cancelled) setPreviewing(false)
      }
    }
    // Debounce so ticking several creators in a row fires a single dry-run.
    const t = setTimeout(runPreview, isManual ? 350 : 0)
    return () => { cancelled = true; clearTimeout(t) }
  }, [campaign.id, isForce, selectedKey, audience, isManual, manualEmpty])

  // ── Real blast mutation ────────────────────────────────────────────────────
  const blastMutation = useMutation({
    mutationFn: () => blastCampaign(campaign.id, {
      force: isForce,
      creator_ids: isManual && selectedIds.length ? selectedIds : undefined,
      audience,
    }),
    onSuccess: (result) => {
      const queued  = result.queued  ?? result.eligible_recipients
      const skipped = result.skipped ?? 0
      const parts = [`${queued} creator${queued !== 1 ? 's' : ''} queued for blast`]
      if (includeAll) {
        const repeats = result.repeat_recipients ?? 0
        if (repeats) parts.push(`${repeats} previously contacted (re-sent)`)
      } else {
        if (result.already_blasted) parts.push(`${result.already_blasted} already blasted (skipped)`)
        if (result.already_applied) parts.push(`${result.already_applied} already applied (skipped)`)
      }
      toast.success(parts.join(' · '), { duration: 7000 })

      void logAdminAction({
        action:    isForce ? 'UPDATE' : 'CREATE',
        tableName: 'cr_campaigns',
        recordId:  campaign.id,
        newValues: { blast_status: 'queued' },
        metadata: {
          event:                 isForce ? 'force_reblast_queued' : 'blast_queued',
          success:               true,
          campaign_id:           campaign.id,
          campaign_name:         campaign.name,
          brand_id:              campaign.brand_id,
          brand_name:            brandName,
          is_force:              isForce,
          // recipient funnel
          matched_creators:      result.matched_creators,
          already_applied:       result.already_applied,
          already_blasted:       result.already_blasted ?? 0,
          eligible_recipients:   result.eligible_recipients,
          // audience choice — 'manual' rides on the 'all' wire value, so log the
          // admin's actual pick, not just what went over the wire
          blast_audience:        choice,
          manual_creator_count:  isManual ? selectedIds.length : 0,
          repeat_recipients:     result.repeat_recipients ?? 0,
          reblasted_recipients:  result.reblasted_recipients ?? 0,
          reapplied_recipients:  result.reapplied_recipients ?? 0,
          // queue outcome at time of blast
          queued_to_send:        queued,
          skipped_build_errors:  skipped,
          // delivery mode
          enable_outbound_email: result.enable_outbound_email ?? false,
          dry_run_note: result.enable_outbound_email === false
            ? 'ENABLE_OUTBOUND_EMAIL is off — emails will be marked skipped by cron'
            : null,
        },
      })

      onBlasted(campaign.id, campaign.name, queued)
      onClose()
    },
    onError: (e: any) => {
      toast.error(e.message ?? 'Blast failed')

      void logAdminAction({
        action:    isForce ? 'UPDATE' : 'CREATE',
        tableName: 'cr_campaigns',
        recordId:  campaign.id,
        metadata: {
          event:         isForce ? 'force_reblast_failed' : 'blast_failed',
          success:       false,
          campaign_id:   campaign.id,
          campaign_name: campaign.name,
          brand_id:      campaign.brand_id,
          brand_name:    brandName,
          is_force:       isForce,
          blast_audience: choice,
          error_message:  e.message ?? 'Unknown error',
          error_code:    e.code    ?? null,
        },
      })
    },
  })

  // ── Derived state ──────────────────────────────────────────────────────────
  const isNoOp = preview != null && preview.eligible_recipients === 0
  const previewReady = preview != null && !previewing && !isNoOp

  // Creators the audience choice can add back. Comes from the worker as a
  // union — the funnel's own already_applied/already_blasted are measured
  // sequentially, so summing them here would under- or over-count.
  const previouslyContacted = preview?.previously_contacted ?? 0
  // How many of those actually made it into the recipient list (post gender filter).
  const repeatRecipients = preview?.repeat_recipients ?? 0

  // Safety net: block non-force fire if campaign is in a locked blast state
  const lockedStatuses = ['in_progress', 'queued']
  const blastLocked = !isForce && lockedStatuses.includes(campaign.blast_status ?? '')

  const nameMatches = nameInput.trim() === campaign.name.trim()
  const canFire = nameMatches && !blastMutation.isPending && !blastLocked

  // ── Step indicators ────────────────────────────────────────────────────────
  function StepIndicator() {
    const previewDone = step === 'confirm'
    return (
      <div className="flex items-center gap-0 mb-3">
        {/* Step 1 */}
        <div className="flex items-center gap-1.5">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ring-2 transition-colors
            ${previewDone
              ? 'bg-green-500 text-white ring-green-200'
              : 'bg-primary text-primary-foreground ring-primary/20'}`}>
            {previewDone ? '✓' : '1'}
          </span>
          <span className={`text-xs font-medium transition-colors ${previewDone ? 'text-green-600' : 'text-foreground'}`}>
            Preview
          </span>
        </div>
        {/* Connector */}
        <div className={`mx-2 h-px w-8 transition-colors ${previewDone ? 'bg-green-300' : 'bg-border'}`} />
        {/* Step 2 */}
        <div className="flex items-center gap-1.5">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ring-2 transition-colors
            ${step === 'confirm'
              ? 'bg-destructive text-destructive-foreground ring-destructive/20'
              : 'bg-muted text-muted-foreground ring-border'}`}>
            2
          </span>
          <span className={`text-xs font-medium transition-colors ${step === 'confirm' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Confirm
          </span>
        </div>
      </div>
    )
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 1 — Preview
  // ══════════════════════════════════════════════════════════════════════════
  if (step === 'preview') {
    return (
      <Dialog open onOpenChange={(open) => !open && !blastMutation.isPending && onClose()}>
        <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 flex flex-col max-h-[90vh]">
          {/* Header band */}
          <div className={`px-6 pt-6 pb-5 border-b shrink-0 ${isForce ? 'bg-orange-50/60' : 'bg-blue-50/40'}`}>
            <StepIndicator />
            <DialogTitle className="flex items-center gap-2.5 text-base">
              {isForce
                ? <span className="flex items-center justify-center w-7 h-7 rounded-full bg-orange-100"><RotateCcw className="h-3.5 w-3.5 text-orange-600" /></span>
                : <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100"><Zap className="h-3.5 w-3.5 text-blue-600" /></span>}
              {isForce ? 'Force Re-blast' : 'Blast Campaign'}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="mt-2 space-y-0.5">
                <p className="text-sm font-medium text-foreground">{campaign.name}</p>
                <p className="text-xs text-muted-foreground">{brandName} · {campaign.product_name}</p>
              </div>
            </DialogDescription>
          </div>

          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Force warning */}
            {isForce && (
              <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-orange-500" />
                <span>
                  Re-blast queues the audience you pick below — by default{' '}
                  <strong>only new eligible creators</strong>, with prior recipients excluded.
                </span>
              </div>
            )}

            {/* Audience: new only · everyone · hand-picked (picker nested inside) */}
            <AudienceSelect
              value={choice}
              onChange={setChoice}
              excluded={previouslyContacted}
              selectedCount={selectedIds.length}
              disabled={previewing}
            >
              <CreatorTestSelect selected={selected} onToggle={toggleCreator} onClear={() => setSelected({})} />
            </AudienceSelect>

            {/* Preview panel */}
            <div className="rounded-xl border overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 border-b">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Live Recipient Preview
                </span>
                {previewing && <Loader2 className="h-3 w-3 ml-auto animate-spin text-muted-foreground" />}
              </div>

              <div className="px-4">
                {manualEmpty && !previewing && (
                  <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-center">
                    <Target className="h-5 w-5 text-amber-400" />
                    <span className="text-sm text-muted-foreground">Pick at least one creator above.</span>
                    <span className="text-xs text-muted-foreground/70">
                      Nothing is sent until someone is selected.
                    </span>
                  </div>
                )}
                {previewing && (
                  <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin text-primary/60" />
                    <span>Querying creator database…</span>
                  </div>
                )}
                {previewError && !previewing && (
                  <div className="flex items-start gap-3 py-5">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 shrink-0 mt-0.5">
                      <XCircle className="h-4 w-4 text-red-500" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-red-700">Preview failed</p>
                      <p className="text-xs text-red-500 mt-0.5 font-mono leading-relaxed">{previewError}</p>
                    </div>
                  </div>
                )}
                {preview && !previewing && (
                  <div className="divide-y divide-border/60">
                    <PreviewRow
                      label={preview.manual_select ? 'Selected creators' : 'Matched by category'}
                      value={preview.matched_creators}
                    />
                    <PreviewRow
                      label="Already applied"
                      value={preview.already_applied}
                      sub={includeAll ? 're-emailing' : 'skipped'}
                    />
                    {preview.applied_and_blasted != null && preview.applied_and_blasted > 0 && (
                      <PreviewRow label="Applied & got blast" value={preview.applied_and_blasted} sub="of applied" />
                    )}
                    {preview.already_blasted != null && preview.already_blasted > 0 && (
                      <PreviewRow
                        label="Already blasted"
                        value={preview.already_blasted}
                        sub={includeAll ? 're-emailing' : 'skipped'}
                      />
                    )}
                    {preview.gender_filter && preview.gender_filter !== 'any' && preview.gender_excluded != null && preview.gender_excluded > 0 && (
                      <PreviewRow label={`Gender filter (${preview.gender_filter} only)`} value={preview.gender_excluded} sub="skipped" />
                    )}
                    <div className="pt-1 pb-0.5">
                      <PreviewRow label="Will be emailed"    value={preview.eligible_recipients} highlight />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* No-op notice */}
            {isNoOp && (
              <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <RefreshCwOff className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
                <span>No new eligible creators — this blast would send <strong>0 emails</strong>. Nothing to do.</span>
              </div>
            )}

            {/* Preview ready cue */}
            {previewReady && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                <span>Preview complete — proceed to confirm when ready.</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 px-6 py-4 bg-muted/20 border-t shrink-0">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={blastMutation.isPending} className="text-muted-foreground">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => setStep('confirm')}
              disabled={!previewReady}
              title={
                manualEmpty ? 'Pick at least one creator'
                : isNoOp    ? 'No eligible creators'
                : previewing ? 'Waiting for preview…'
                : undefined
              }
              className="gap-1.5"
            >
              Proceed to confirm
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 2 — Confirm (danger zone)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Dialog open onOpenChange={(open) => !open && !blastMutation.isPending && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 border-red-200/80 flex flex-col max-h-[90vh]">
        {/* Header band */}
        <div className="px-6 pt-6 pb-5 border-b bg-red-50/50 shrink-0">
          <StepIndicator />
          <DialogTitle className="flex items-center gap-2.5 text-base text-destructive">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-red-100">
              <ShieldAlert className="h-3.5 w-3.5 text-red-600" />
            </span>
            Confirm — this sends real emails
          </DialogTitle>
          <DialogDescription asChild>
            <div className="mt-2">
              <p className="text-sm font-medium text-foreground">{campaign.name}</p>
              <p className="text-xs text-muted-foreground">{brandName}</p>
            </div>
          </DialogDescription>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Recipient count callout */}
          <div className="rounded-xl border-2 border-red-200 bg-gradient-to-b from-red-50 to-red-50/30 p-5 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-red-400 mb-2">
              {isForce ? 'Force re-blast will email' : 'Blast will email'}
            </p>
            <p className="text-5xl font-bold text-red-600 tabular-nums leading-none">
              {preview?.eligible_recipients ?? 0}
            </p>
            <p className="text-sm text-red-400 mt-2">
              creator{(preview?.eligible_recipients ?? 0) !== 1 ? 's' : ''} · <span className="font-medium">cannot be undone</span>
            </p>
          </div>

          {/* Audience summary — restates the step-1 choice right before firing */}
          {isManual ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <Target className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
              <span>
                Audience: <strong>{selectedIds.length} hand-picked creator{selectedIds.length !== 1 ? 's' : ''}</strong> —
                category &amp; gender targeting skipped, and they are emailed whether or not they were
                contacted before.
              </span>
            </div>
          ) : (
            <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
              includeAll
                ? 'border-orange-200 bg-orange-50 text-orange-800'
                : 'border-slate-200 bg-slate-50 text-slate-700'
            }`}>
              {includeAll
                ? <Repeat   className="h-4 w-4 mt-0.5 shrink-0 text-orange-500" />
                : <UserPlus className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />}
              <span>
                {includeAll ? (
                  <>
                    Audience: <strong>everyone</strong> — including{' '}
                    <strong>{repeatRecipients}</strong> creator
                    {repeatRecipients !== 1 ? 's' : ''} contacted before
                    {(preview?.reapplied_recipients ?? 0) > 0 && (
                      <> ({preview?.reblasted_recipients ?? 0} already emailed,{' '}
                      {preview?.reapplied_recipients ?? 0} already applied)</>
                    )}
                    , who will hear about this campaign a second time.
                  </>
                ) : (
                  <>
                    Audience: <strong>new creators only</strong> — {previouslyContacted} creator
                    {previouslyContacted !== 1 ? 's' : ''} already emailed or applied will be skipped.
                  </>
                )}
              </span>
            </div>
          )}

          {/* In-progress/queued guard */}
          {blastLocked && (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
              <span>
                Status is <strong>{campaign.blast_status}</strong> — a blast is already running. Use{' '}
                <strong>Re-blast</strong> (force mode) to queue new creators.
              </span>
            </div>
          )}

          {/* Typed-name confirmation */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Type the campaign name to confirm
            </label>
            <div className="font-mono text-xs text-muted-foreground bg-muted/60 border rounded-md px-3 py-1.5 select-all">
              {campaign.name}
            </div>
            <Input
              placeholder="Type campaign name…"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              className={nameInput.length > 0
                ? nameMatches
                  ? 'border-green-400 focus-visible:ring-green-300'
                  : 'border-red-300 focus-visible:ring-red-200'
                : ''}
              disabled={blastMutation.isPending}
              autoFocus
            />
            {nameInput.length > 0 && !nameMatches && (
              <p className="text-xs text-red-500">Doesn't match — check capitalization and spacing.</p>
            )}
            {nameMatches && (
              <p className="text-xs text-green-600 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" /> Name confirmed
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 bg-muted/20 border-t shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStep('preview'); setNameInput('') }}
            disabled={blastMutation.isPending}
            className="gap-1.5 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => blastMutation.mutate()}
            disabled={!canFire}
            className="gap-1.5"
            title={
              blastLocked    ? 'Blast already running — use force re-blast'
              : !nameMatches ? 'Type the campaign name to unlock'
              : undefined
            }
          >
            {blastMutation.isPending
              ? <><Loader2 className="h-4 w-4 animate-spin" />Queueing…</>
              : isForce
                ? <><RotateCcw className="h-4 w-4" />Fire re-blast ({preview?.eligible_recipients})</>
                : <><Zap className="h-4 w-4" />Fire blast ({preview?.eligible_recipients})</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Blast status monitor ──────────────────────────────────────────────────────
//
// Shown as a banner above the table after a blast is fired.
// Polls cr_campaigns (blast_status/counts) + cr_email_queue (per-email status)
// every 5 seconds. Stops automatically on terminal state and auto-dismisses
// 8 seconds later.

interface BlastMonitor {
  campaignId: string
  campaignName: string
  recipientCount: number
}

interface QueueStats {
  queued: number
  sending: number
  sent: number
  failed: number
  skipped: number
}

const TERMINAL_BLAST_STATUSES = ['blast_sent', 'blast_failed', 'blast_skipped']

// How long to wait for the cron to start draining before declaring a stall.
// Cron runs every minute; 10 min = 10 missed ticks before we give up polling.
const CRON_STALL_MS = 10 * 60 * 1000

function BlastStatusMonitor({ monitor, onDismiss }: { monitor: BlastMonitor; onDismiss: () => void }) {
  const [cronStalled, setCronStalled] = useState(false)
  // Ref so the stall timer can read latest progress without a re-render race
  const hasProgressRef = useRef(false)

  // Poll campaign-level blast_status
  const { data: campaignPoll } = useQuery({
    queryKey: ['blast-poll-campaign', monitor.campaignId],
    enabled: !cronStalled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_campaigns')
        .select('blast_status,blast_recipient_count,blast_completed_at')
        .eq('id', monitor.campaignId)
        .limit(1)
      if (error) throw error
      return (data ?? [])[0] as {
        blast_status: string
        blast_recipient_count: number | null
        blast_completed_at: string | null
      } | undefined
    },
    refetchInterval: (query) => {
      const status = query.state.data?.blast_status ?? ''
      return TERMINAL_BLAST_STATUSES.includes(status) ? false : 5000
    },
  })

  // Poll email queue for per-status counts using HEAD requests (count only, zero rows
  // transferred). This avoids the PostgREST max_rows cap (typically 1000) that caused
  // the monitor to stop updating after the first 1000 rows were sent.
  const { data: stats } = useQuery({
    queryKey: ['blast-poll-queue', monitor.campaignId],
    enabled: !cronStalled,
    queryFn: async (): Promise<QueueStats> => {
      const countByStatus = (status: string) =>
        supabase
          .from('cr_email_queue')
          .select('*', { count: 'exact', head: true })
          .eq('campaign_id', monitor.campaignId)
          .eq('email_type', 'campaign_blast')
          .eq('status', status)

      const [queuedRes, sendingRes, sentRes, failedRes, skippedRes] = await Promise.all([
        countByStatus('queued'),
        countByStatus('sending'),
        countByStatus('sent'),
        countByStatus('failed'),
        countByStatus('skipped'),
      ])

      return {
        queued:  queuedRes.count  ?? 0,
        sending: sendingRes.count ?? 0,
        sent:    sentRes.count    ?? 0,
        failed:  failedRes.count  ?? 0,
        skipped: skippedRes.count ?? 0,
      }
    },
    refetchInterval: (query) => {
      const s = query.state.data
      if (!s) return 5000
      const hasActive = s.queued > 0 || s.sending > 0
      return hasActive ? 5000 : false
    },
  })

  const safeStats: QueueStats = stats ?? { queued: 0, sending: 0, sent: 0, failed: 0, skipped: 0 }

  const blastStatus = campaignPoll?.blast_status ?? 'queued'
  const isTerminal = TERMINAL_BLAST_STATUSES.includes(blastStatus)
  const isSuccess  = blastStatus === 'blast_sent'
  const isFailure  = blastStatus === 'blast_failed'

  const total    = monitor.recipientCount || campaignPoll?.blast_recipient_count || 1
  const done     = safeStats.sent + safeStats.failed + safeStats.skipped
  const progress = Math.min(100, Math.round((done / total) * 100))

  // Keep hasProgressRef current so the stall timer can check it without a stale closure
  useEffect(() => {
    if (safeStats.sent > 0 || safeStats.sending > 0 || isTerminal || blastStatus === 'in_progress') {
      hasProgressRef.current = true
    }
  }, [safeStats.sent, safeStats.sending, isTerminal, blastStatus])

  // One-shot stall detector: if the cron hasn't touched anything within CRON_STALL_MS, stop polling
  useEffect(() => {
    const t = setTimeout(() => {
      if (!hasProgressRef.current) setCronStalled(true)
    }, CRON_STALL_MS)
    return () => clearTimeout(t)
  }, [])

  // Auto-dismiss 8s after reaching terminal state
  useEffect(() => {
    if (!isTerminal) return
    const t = setTimeout(onDismiss, 8000)
    return () => clearTimeout(t)
  }, [isTerminal, onDismiss])

  // ── Banner colour depending on state ──
  const bannerClass = isSuccess
    ? 'border-green-200 bg-green-50'
    : isFailure
      ? 'border-red-200 bg-red-50'
      : cronStalled
        ? 'border-yellow-200 bg-yellow-50'
        : 'border-blue-200 bg-blue-50'

  const titleColor = isSuccess    ? 'text-green-800'
    : isFailure    ? 'text-red-800'
    : cronStalled  ? 'text-yellow-800'
    : 'text-blue-800'
  const subColor   = isSuccess    ? 'text-green-600'
    : isFailure    ? 'text-red-600'
    : cronStalled  ? 'text-yellow-700'
    : 'text-blue-600'

  return (
    <div className={`rounded-lg border p-4 ${bannerClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Status icon */}
          <div className="mt-0.5 shrink-0">
            {isSuccess              && <CheckCircle2   className="h-5 w-5 text-green-600" />}
            {isFailure              && <XCircle        className="h-5 w-5 text-red-600" />}
            {cronStalled            && <AlertTriangle  className="h-5 w-5 text-yellow-500" />}
            {!isTerminal && !cronStalled && <Radio     className="h-5 w-5 text-blue-600 animate-pulse" />}
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            {/* Title */}
            <div>
              <p className={`font-semibold text-sm ${titleColor}`}>
                {isSuccess    ? 'Blast complete'
                : isFailure   ? 'Blast failed'
                : cronStalled ? 'Blast stalled — cron not responding'
                : 'Blast in progress'}
              </p>
              <p className={`text-xs truncate ${subColor}`}>{monitor.campaignName}</p>
            </div>

            {/* Progress bar — only while running */}
            {!isTerminal && (
              <Progress value={progress} className="h-1.5" />
            )}

            {/* Stats row */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {safeStats.sent > 0 && (
                <span className="text-green-700 font-medium">✓ {safeStats.sent} sent</span>
              )}
              {(safeStats.sending > 0 || (!isTerminal && safeStats.queued > 0)) && (
                <span className="text-blue-700">
                  {safeStats.sending > 0 ? `${safeStats.sending} sending` : ''}
                  {safeStats.sending > 0 && safeStats.queued > 0 ? ' · ' : ''}
                  {safeStats.queued > 0 ? `${safeStats.queued} queued` : ''}
                </span>
              )}
              {safeStats.failed > 0 && (
                <span className="text-red-600 font-medium">✗ {safeStats.failed} failed</span>
              )}
              {safeStats.skipped > 0 && (
                <span className="text-muted-foreground">{safeStats.skipped} skipped</span>
              )}
              {!isTerminal && done === 0 && (
                <span className={`${subColor} flex items-center gap-1`}>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Waiting for cron to start draining…
                </span>
              )}
              {isTerminal && campaignPoll?.blast_completed_at && (
                <span className="text-muted-foreground">
                  Completed {format(new Date(campaignPoll.blast_completed_at), 'h:mm a')}
                </span>
              )}
              {isTerminal && (
                <span className={`${subColor} text-xs`}>Dismissing automatically…</span>
              )}
            </div>

            {/* Retry hint on failure */}
            {isFailure && (
              <p className="text-xs text-red-600">
                Use the <strong>Re-blast</strong> button on the campaign row to retry.
              </p>
            )}

            {/* Cron stall warning */}
            {cronStalled && !isTerminal && (
              <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-yellow-500" />
                <span>
                  Cron hasn't started in 10 minutes — polling stopped.
                  Emails may be stuck. Check the worker deployment or cron schedule.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className={`shrink-0 rounded p-1 hover:bg-black/5 transition-colors ${titleColor}`}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// ── Outbound email status badge ───────────────────────────────────────────────
//
// Polls GET /api/admin/blast-status every 20 seconds and shows whether
// outbound email is enabled (green) or in dry-run mode (yellow),
// plus a live queue depth summary.

function OutboundEmailStatus() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['worker-blast-status'],
    queryFn: getBlastStatus,
    retry: 2,
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Checking email status…</span>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500">
        <span className="h-2 w-2 rounded-full bg-gray-300" />
        Email status unavailable
      </div>
    )
  }

  const { enable_outbound_email } = data
  const queue = data.queue ?? { queued: 0, sending: 0, failed: 0, skipped: 0 }
  const hasQueueActivity = queue.queued > 0 || queue.sending > 0 || queue.failed > 0 || queue.skipped > 0

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* ON / OFF badge */}
      <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium
        ${enable_outbound_email
          ? 'border-green-200 bg-green-50 text-green-800'
          : 'border-yellow-200 bg-yellow-50 text-yellow-800'}`}>
        <span className={`h-2 w-2 rounded-full ${enable_outbound_email ? 'bg-green-500 animate-pulse' : 'bg-yellow-400'}`} />
        {enable_outbound_email
          ? <><Mail className="h-3.5 w-3.5" /> Outbound emails ON</>
          : <><MailX className="h-3.5 w-3.5" /> Outbound emails OFF <span className="font-normal opacity-70">(dry-run mode)</span></>}
      </div>

      {/* Queue depth — only when there's something to show */}
      {hasQueueActivity && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {queue.queued > 0 && (
            <span className="rounded bg-indigo-50 border border-indigo-200 text-indigo-700 px-2 py-0.5">
              {queue.queued} queued
            </span>
          )}
          {queue.sending > 0 && (
            <span className="rounded bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5">
              {queue.sending} sending
            </span>
          )}
          {queue.failed > 0 && (
            <span className="rounded bg-red-50 border border-red-200 text-red-700 px-2 py-0.5">
              {queue.failed} failed
            </span>
          )}
          {queue.skipped > 0 && (
            <span className="rounded bg-orange-50 border border-orange-200 text-orange-700 px-2 py-0.5">
              {queue.skipped} skipped
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ── Email queue inspector ─────────────────────────────────────────────────────

const QUEUE_STATUS_COLORS: Record<string, string> = {
  queued:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  sending: 'bg-blue-100 text-blue-800 border-blue-200',
  sent:    'bg-green-100 text-green-800 border-green-200',
  failed:  'bg-red-100 text-red-800 border-red-200',
  skipped: 'bg-orange-100 text-orange-800 border-orange-200',
}

function QueueStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={QUEUE_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'}>
      {status}
    </Badge>
  )
}

interface EmailQueueInspectorProps {
  campaigns: CrCampaign[]
  brandById: Map<string, string>
}

type DatePreset = 'all' | 'today' | 'yesterday' | 'last7d' | 'last30d' | 'custom'

function presetToRange(preset: DatePreset, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date()
  if (preset === 'today')     return { from: startOfDay(now).toISOString() }
  if (preset === 'yesterday') return { from: startOfDay(subDays(now, 1)).toISOString(), to: endOfDay(subDays(now, 1)).toISOString() }
  if (preset === 'last7d')    return { from: startOfDay(subDays(now, 6)).toISOString() }
  if (preset === 'last30d')   return { from: startOfDay(subDays(now, 29)).toISOString() }
  if (preset === 'custom')    return {
    from: customFrom ? startOfDay(new Date(customFrom)).toISOString() : undefined,
    to:   customTo   ? endOfDay(new Date(customTo)).toISOString()     : undefined,
  }
  return {}
}

function EmailQueueInspector({ campaigns, brandById }: EmailQueueInspectorProps) {
  const [campaignFilter, setCampaignFilter] = useState('all')
  const [statusFilter, setStatusFilter]     = useState('all')
  const [datePreset, setDatePreset]         = useState<DatePreset>('all')
  const [customFrom, setCustomFrom]         = useState('')
  const [customTo, setCustomTo]             = useState('')
  const [expandedId, setExpandedId]         = useState<string | null>(null)
  const [emailSearchInput, setEmailSearchInput] = useState('')
  const [emailSearch, setEmailSearch]           = useState('')   // debounced
  const [currentPage, setCurrentPage]           = useState(1)
  const [pageSize, setPageSize]                 = useState(25)

  // Debounce email search 300 ms
  useEffect(() => {
    const t = setTimeout(() => { setEmailSearch(emailSearchInput.trim()); setCurrentPage(1) }, 300)
    return () => clearTimeout(t)
  }, [emailSearchInput])

  const { from: scheduledFrom, to: scheduledTo } = presetToRange(datePreset, customFrom, customTo)

  function resetPage() { setCurrentPage(1); setExpandedId(null) }

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['email-queue-inspector', campaignFilter, statusFilter, datePreset, customFrom, customTo, emailSearch, currentPage, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('cr_email_queue')
        .select(
          'id,campaign_id,email_type,status,recipient_email,attempts,last_error,sent_at,last_attempt_at,scheduled_for',
          { count: 'exact' }
        )
        .order('scheduled_for', { ascending: false })
        // Bulk creator outreach (the Trybe sequence) shares this table but not
        // this screen: one run is thousands of rows and would bury the hire,
        // payment and application mail this inspector exists to show. It is
        // reported per creator on /trybe-outreach instead. Mirrors
        // BULK_OUTREACH_EMAIL_TYPE_PREFIX in the worker's applications.ts.
        .not('email_type', 'like', 'creator_outreach_%')

      if (emailSearch)              query = query.ilike('recipient_email', `%${emailSearch}%`)
      if (campaignFilter !== 'all') query = query.eq('campaign_id', campaignFilter)
      if (statusFilter   !== 'all') query = query.eq('status', statusFilter)
      if (scheduledFrom)            query = query.gte('scheduled_for', scheduledFrom)
      if (scheduledTo)              query = query.lte('scheduled_for', scheduledTo)

      const { data, count, error } = await query.range(
        (currentPage - 1) * pageSize,
        currentPage * pageSize - 1
      )
      if (error) throw error
      return { rows: (data ?? []) as EmailQueueRow[], total: count ?? 0 }
    },
  })

  const pagedRows = data?.rows ?? []
  const totalRows = data?.total ?? 0
  const totalPages = Math.ceil(totalRows / pageSize)
  const campaignById = new Map(campaigns.map(c => [c.id, c]))

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3">
        {/* filters row + refresh */}
        <div className="flex items-center gap-3 flex-wrap justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search by email…"
                value={emailSearchInput}
                onChange={e => { setEmailSearchInput(e.target.value); setExpandedId(null) }}
                className="pl-8 w-52 h-9"
              />
            </div>
            <Select value={campaignFilter} onValueChange={v => { setCampaignFilter(v); resetPage() }}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="All campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campaigns</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); resetPage() }}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="sending">Sending</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
              </SelectContent>
            </Select>
            <Select value={datePreset} onValueChange={v => { setDatePreset(v as DatePreset); resetPage() }}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last7d">Last 7 days</SelectItem>
                <SelectItem value="last30d">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Custom date range inputs — only visible when preset=custom */}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground font-medium shrink-0">Scheduled from</span>
            <input
              type="date"
              value={customFrom}
              onChange={e => { setCustomFrom(e.target.value); resetPage() }}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={e => { setCustomTo(e.target.value); resetPage() }}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {(customFrom || customTo) && (
              <button
                onClick={() => { setCustomFrom(''); setCustomTo(''); resetPage() }}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Says out loud what the query filters out, so a missing Trybe email
          reads as "it lives elsewhere" rather than "it was never sent". */}
      <p className="text-xs text-muted-foreground">
        Job operations mail only. Trybe outreach sends share this queue but are listed per creator on{' '}
        <Link to="/trybe-outreach" className="underline hover:text-foreground">Trybe Outreach</Link>.
      </p>

      {/* Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6" />
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Tries</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Sent at</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                    No queue rows found
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.flatMap(row => {
                  const isFailed  = row.status === 'failed' || row.status === 'skipped'
                  const isExpanded = expandedId === row.id
                  const campaign  = row.campaign_id ? campaignById.get(row.campaign_id) : null
                  const brand     = campaign ? (brandById.get(campaign.brand_id) ?? null) : null

                  return [
                    <TableRow
                      key={row.id}
                      className={`${isFailed ? 'bg-red-50/40 hover:bg-red-50/60' : ''} ${row.last_error ? 'cursor-pointer' : ''}`}
                      onClick={() => row.last_error && setExpandedId(isExpanded ? null : row.id)}
                    >
                      {/* expand toggle */}
                      <TableCell className="pr-0 w-6">
                        {row.last_error ? (
                          isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <QueueStatusBadge status={row.status} />
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-muted-foreground">
                          {row.email_type.replace(/_/g, ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-sm">{row.recipient_email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {campaign ? (
                          <div className="max-w-40">
                            <p className="truncate" title={campaign.name}>{campaign.name}</p>
                            {brand && <p className="text-xs truncate opacity-60">{brand}</p>}
                          </div>
                        ) : row.campaign_id ? (
                          <span className="font-mono text-xs">{row.campaign_id.slice(0, 8)}…</span>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{row.attempts}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {row.scheduled_for ? format(new Date(row.scheduled_for), 'MMM d, h:mm a') : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {row.sent_at ? format(new Date(row.sent_at), 'MMM d, h:mm a') : '—'}
                      </TableCell>
                    </TableRow>,

                    // Expanded error detail row
                    ...(isExpanded && row.last_error ? [
                      <TableRow key={`${row.id}-error`} className="bg-red-50/60 hover:bg-red-50/60">
                        <TableCell colSpan={8} className="py-3 px-5">
                          <div className="flex items-start gap-2.5">
                            <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-red-700 mb-1">Error detail</p>
                              <pre className="text-xs text-red-600 font-mono whitespace-pre-wrap break-all leading-relaxed bg-red-100/60 rounded p-2">
                                {row.last_error}
                              </pre>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>,
                    ] : []),
                  ]
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <DataPagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={totalRows}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1) }}
      />
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CampaignOps() {
  const [searchInput, setSearchInput]   = useState('')
  const [search, setSearch]             = useState('')        // debounced
  const [statusFilter, setStatusFilter] = useState('all')
  const [blastFilter, setBlastFilter]   = useState('all')
  const [sortField, setSortField]       = useState('created_at')
  const [sortAsc, setSortAsc]           = useState(false)
  const [currentPage, setCurrentPage]   = useState(1)
  const [pageSize, setPageSize]         = useState(25)
  const [dialogCampaign, setDialogCampaign]     = useState<CrCampaign | null>(null)
  const [dialogIsForce, setDialogIsForce]       = useState(false)
  const [activeMonitor, setActiveMonitor]       = useState<BlastMonitor | null>(null)
  const dismissedMonitorsRef = useRef<Set<string>>(new Set())
  const activeMonitorRef = useRef<BlastMonitor | null>(null)
  activeMonitorRef.current = activeMonitor

  const queryClient = useQueryClient()

  // Debounce search input by 300 ms
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1) }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  function handleStatusFilterChange(value: string) {
    setStatusFilter(value)
    setCurrentPage(1)
  }
  function handleBlastFilterChange(value: string) {
    setBlastFilter(value)
    setCurrentPage(1)
  }

  function toggleSort(field: string) {
    setCurrentPage(1)
    if (sortField === field) setSortAsc(a => !a)
    else { setSortField(field); setSortAsc(true) }
  }

  // Fetch cr_campaigns via admin-proxy (Creator App Supabase) — DB-level filtering + pagination
  const { data: campaignsData, isLoading: loadingCampaigns, refetch, isFetching } = useQuery({
    queryKey: ['campaign-ops-campaigns', search, statusFilter, blastFilter, sortField, sortAsc, currentPage, pageSize],
    refetchInterval: (query) => {
      const rows = query.state.data?.data ?? []
      const hasActiveBlast = rows.some(
        (c: CrCampaign) => c.blast_status === 'queued' || c.blast_status === 'in_progress'
      )
      return hasActiveBlast ? 60000 : false
    },
    queryFn: async () => {
      const q = search.trim()

      let query = supabase
        .from('cr_campaigns')
        .select(
          'id,brand_id,name,product_name,status,blast_status,blast_recipient_count,blast_sent_count,blast_failed_count,blast_queued_at,blast_completed_at,reblast_count,created_at,is_viral_remake',
          { count: 'exact' }
        )
        // brand_name lives in a separate table — fall back to created_at for server sort
        .order(sortField === 'brand_name' ? 'created_at' : sortField, { ascending: sortAsc })

      if (q) {
        // Pre-fetch matching brand IDs so brand-name search works across the join
        const { data: matchingBrands } = await supabase
          .from('brand_configs')
          .select('id')
          .ilike('brand_name', `%${q}%`)
        const matchingBrandIds: string[] = (matchingBrands ?? []).map((b: any) => b.id)

        if (matchingBrandIds.length > 0) {
          query = query.or(
            `name.ilike.%${q}%,product_name.ilike.%${q}%,brand_id.in.(${matchingBrandIds.join(',')})`
          )
        } else {
          query = query.or(`name.ilike.%${q}%,product_name.ilike.%${q}%`)
        }
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      if (blastFilter !== 'all') {
        if (blastFilter === 'awaiting_blast') {
          query = query.or('blast_status.eq.awaiting_blast,blast_status.is.null')
        } else {
          query = query.eq('blast_status', blastFilter)
        }
      }

      const { data, count, error } = await query.range(
        (currentPage - 1) * pageSize,
        currentPage * pageSize - 1
      )
      if (error) throw error
      return { data: (data ?? []) as CrCampaign[], total: count ?? 0 }
    },
  })

  const campaigns = campaignsData?.data ?? []
  const totalCampaigns = campaignsData?.total ?? 0
  const totalPages = Math.ceil(totalCampaigns / pageSize)

  // Fetch brand names for campaigns on the current page
  const brandIds = [...new Set(campaigns.map(c => c.brand_id).filter(Boolean))]
  const { data: brands } = useQuery({
    queryKey: ['campaign-ops-brands', brandIds.join(',')],
    queryFn: async () => {
      if (!brandIds.length) return [] as BrandConfig[]
      const { data, error } = await supabase
        .from('brand_configs')
        .select('id,brand_name')
        .in('id', brandIds)
      if (error) throw error
      return (data ?? []) as BrandConfig[]
    },
    enabled: brandIds.length > 0,
  })

  const brandById = new Map((brands ?? []).map(b => [b.id, b.brand_name]))

  // Client-side brand sort (brand_name is in a separate lookup table, can't sort server-side)
  const sortedCampaigns = sortField === 'brand_name'
    ? [...campaigns].sort((a, b) => {
        const na = brandById.get(a.brand_id) ?? ''
        const nb = brandById.get(b.brand_id) ?? ''
        return sortAsc ? na.localeCompare(nb) : nb.localeCompare(na)
      })
    : campaigns

  function SortHeader({ field, children, className }: { field: string; children: React.ReactNode; className?: string }) {
    const active = sortField === field
    return (
      <TableHead
        className={`cursor-pointer select-none whitespace-nowrap ${className ?? ''}`}
        onClick={() => toggleSort(field)}
      >
        <div className={`flex items-center gap-1 ${className?.includes('text-right') ? 'justify-end' : ''}`}>
          {children}
          {active
            ? sortAsc
              ? <ArrowUp   className="h-3 w-3 text-primary" />
              : <ArrowDown className="h-3 w-3 text-primary" />
            : <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
        </div>
      </TableHead>
    )
  }

  // Auto-activate monitor for any blast already in-progress on the current page.
  // Uses activeMonitorRef (synced during render) instead of activeMonitor state
  // in the dep array — this prevents the effect from firing when dismiss sets
  // activeMonitor to null, which would otherwise immediately re-activate it.
  useEffect(() => {
    if (!campaigns?.length || activeMonitorRef.current) return
    const inProgress = campaigns.find(c =>
      (c.blast_status === 'in_progress' || c.blast_status === 'queued') &&
      !dismissedMonitorsRef.current.has(c.id)
    )
    if (!inProgress) return
    setActiveMonitor({
      campaignId:     inProgress.id,
      campaignName:   inProgress.name,
      recipientCount: inProgress.blast_recipient_count ?? 0,
    })
  }, [campaigns])

  function handleMonitorDismiss() {
    if (activeMonitor) {
      dismissedMonitorsRef.current.add(activeMonitor.campaignId)
      queryClient.invalidateQueries({ queryKey: ['campaign-ops-campaigns'] })
    }
    setActiveMonitor(null)
  }

  function openBlast(campaign: CrCampaign, force: boolean) {
    setDialogCampaign(campaign)
    setDialogIsForce(force)
  }

  function onBlasted(campaignId: string, campaignName: string, recipientCount: number) {
    queryClient.invalidateQueries({ queryKey: ['campaign-ops-campaigns'] })
    setActiveMonitor({ campaignId, campaignName, recipientCount })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Job Operations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View job blast status and trigger blasts for active jobs
            </p>
          </div>
          <OutboundEmailStatus />
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="shrink-0">
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Live blast monitor */}
      {activeMonitor && (
        <BlastStatusMonitor
          monitor={activeMonitor}
          onDismiss={handleMonitorDismiss}
        />
      )}

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns">Jobs</TabsTrigger>
          <TabsTrigger value="queue">Queue Inspector</TabsTrigger>
        </TabsList>

        {/* ── Campaigns tab ── */}
        <TabsContent value="campaigns" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaign, brand, product..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Campaign status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={blastFilter} onValueChange={handleBlastFilterChange}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Blast status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All blast statuses</SelectItem>
                <SelectItem value="awaiting_blast">Awaiting blast</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="blast_sent">Blast sent</SelectItem>
                <SelectItem value="blast_failed">Blast failed</SelectItem>
                <SelectItem value="blast_skipped">Blast skipped</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <Card>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHeader field="name">Campaign</SortHeader>
                    <TableHead>Brand</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Blast Status</TableHead>
                    <SortHeader field="blast_recipient_count" className="text-right">Recipients</SortHeader>
                    <SortHeader field="blast_sent_count" className="text-right">Sent</SortHeader>
                    <SortHeader field="blast_failed_count" className="text-right">Failed</SortHeader>
                    <SortHeader field="created_at">Created</SortHeader>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingCampaigns ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : campaigns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-10">
                        No jobs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCampaigns.map(c => {
                      const isActive = c.status === 'active'
                      const canBlast = isActive && BLASTABLE_STATUSES.includes(c.blast_status)
                      const canForce = isActive && FORCE_REQUIRED_STATUSES.includes(c.blast_status)

                      return (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium max-w-64" title={c.name}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="truncate min-w-0">{c.name}</span>
                              {/* Full label per product feedback — widened the
                                  cell (was 12rem) rather than abbreviating it. */}
                              {isViralRemake(c) && <ViralRemakeBadge className="shrink-0" />}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {brandById.get(c.brand_id) ?? (
                              <span className="text-muted-foreground font-mono text-xs">
                                {c.brand_id.slice(0, 8)}…
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-36 truncate" title={c.product_name}>
                            {c.product_name}
                          </TableCell>
                          <TableCell>
                            <CampaignStatusBadge status={c.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <BlastStatusBadge status={c.blast_status} />
                              {c.reblast_count != null && c.reblast_count > 0 && (
                                <span className="text-[10px] font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                                  ×{c.reblast_count + 1} blasts
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {c.blast_recipient_count ?? '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-green-700">
                            {c.blast_sent_count ?? '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-red-600">
                            {c.blast_failed_count != null && c.blast_failed_count > 0
                              ? c.blast_failed_count
                              : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {c.created_at ? format(new Date(c.created_at), 'MMM d, yyyy') : '—'}
                          </TableCell>
                          <TableCell>
                            {canBlast && (
                              <Button
                                size="sm"
                                variant="default"
                                className="h-7 px-3 text-xs"
                                onClick={() => openBlast(c, false)}
                              >
                                <Zap className="h-3 w-3 mr-1" />
                                Blast
                              </Button>
                            )}
                            {canForce && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                                onClick={() => openBlast(c, true)}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Re-blast
                              </Button>
                            )}
                            {!isActive && (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalCampaigns}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1) }}
          />
        </TabsContent>

        {/* ── Queue Inspector tab ── */}
        <TabsContent value="queue" className="mt-4">
          <EmailQueueInspector campaigns={campaigns ?? []} brandById={brandById} />
        </TabsContent>
      </Tabs>

      {/* Blast dialog */}
      {dialogCampaign && (
        <BlastDialog
          campaign={dialogCampaign}
          brandName={brandById.get(dialogCampaign.brand_id) ?? dialogCampaign.brand_id}
          isForce={dialogIsForce}
          onClose={() => setDialogCampaign(null)}
          onBlasted={onBlasted}
        />
      )}
    </div>
  )
}
